import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  fetchPortalAddonPreview,
  startPortalAddonPurchase,
  uploadPortalAddonPurchaseInstapayProof,
  type PortalAddonPaymentProvider,
  type PortalAddonSlug,
} from '@/modules/portal/services/portalAddonPurchase.api';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';

const INSTAPAY_RECIPIENT_ID = 'amwajtechnology@instapay';
const INSTAPAY_RECIPIENT_PHONE = '01031846418';

type PortalAddonCardProps = {
  addonSlug: PortalAddonSlug;
  locale: string;
  supportsQuantity?: boolean;
  className?: string;
};

const ADDON_LABELS: Record<
  PortalAddonSlug,
  { ar: string; en: string; icon: typeof Icons.Package }
> = {
  ai_tokens_200: { ar: 'باقة 200 توكين', en: '200 Tokens Pack', icon: Icons.CheckCircle },
  ai_tokens_500: { ar: 'باقة 500 توكين', en: '500 Tokens Pack', icon: Icons.CheckCircle },
  ai_tokens_1000: { ar: 'باقة 1000 توكين', en: '1000 Tokens Pack', icon: Icons.CheckCircle },
};

export function PortalAddonCard({
  addonSlug,
  locale,
  supportsQuantity = false,
  className,
}: PortalAddonCardProps) {
  const language = locale.startsWith('ar') ? 'ar' : 'en';
  const isAr = language === 'ar';
  const [quantity, setQuantity] = useState(1);
  const [isPurchasing, setIsPurchasing] = useState<PortalAddonPaymentProvider | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  const [instapayPaymentId, setInstapayPaymentId] = useState<string | null>(null);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [isUploadingProof, setIsUploadingProof] = useState(false);
  const [proofUploaded, setProofUploaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const label = ADDON_LABELS[addonSlug];
  const Icon = label.icon;

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'addon-preview', addonSlug, quantity],
    queryFn: () => fetchPortalAddonPreview(addonSlug, quantity),
    retry: false,
    enabled: !instapayPaymentId,
  });

  // Not available for the tenant's current plan tier (backend is the source of truth) — hide silently.
  if (error) {
    return null;
  }

  const priceLabel = data
    ? new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: data.currency,
        maximumFractionDigits: 2,
      }).format(data.total_minor / 100)
    : null;

  const handlePurchase = async (paymentProvider: PortalAddonPaymentProvider) => {
    setIsPurchasing(paymentProvider);
    setPurchaseError(null);
    try {
      const result = await startPortalAddonPurchase({
        addonSlug,
        quantity,
        paymentProvider,
        locale: language,
        idempotencyKey: crypto.randomUUID(),
      });
      if (result.payment_provider === 'myfatoorah') {
        window.location.href = result.payment_url;
      } else {
        setInstapayPaymentId(result.payment_id);
      }
    } catch {
      setPurchaseError(isAr ? 'حدث خطأ، حاول مرة أخرى' : 'Something went wrong, please try again');
    } finally {
      setIsPurchasing(null);
    }
  };

  const handleProofFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setProofFile(file);
  };

  const handleUploadProof = async () => {
    if (!instapayPaymentId || !proofFile) return;
    setIsUploadingProof(true);
    setPurchaseError(null);
    try {
      await uploadPortalAddonPurchaseInstapayProof(instapayPaymentId, proofFile);
      setProofUploaded(true);
    } catch {
      setPurchaseError(
        isAr ? 'تعذر رفع الإيصال، حاول مرة أخرى' : 'Could not upload receipt, try again',
      );
    } finally {
      setIsUploadingProof(false);
    }
  };

  return (
    <div className={cn('portal-glass-panel rounded-2xl border p-5', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">{isAr ? label.ar : label.en}</p>
            {isLoading ? (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {isAr ? 'جارِ التحميل...' : 'Loading...'}
              </p>
            ) : priceLabel ? (
              <p className="mt-0.5 text-sm font-medium text-foreground">
                {priceLabel}
                <span className="text-xs text-muted-foreground">
                  {' '}
                  /{' '}
                  {data?.billing_period === 'yearly'
                    ? isAr
                      ? 'سنويًا'
                      : 'yr'
                    : isAr
                      ? 'شهريًا'
                      : 'mo'}
                </span>
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {!instapayPaymentId && supportsQuantity ? (
        <div className="mt-4 flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{isAr ? 'العدد' : 'Quantity'}</span>
          <div className="flex items-center gap-1 rounded-lg border">
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
              aria-label={isAr ? 'إنقاص العدد' : 'Decrease quantity'}
            >
              −
            </button>
            <span className="min-w-[2ch] text-center text-sm font-medium">{quantity}</span>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40"
              onClick={() => setQuantity((q) => Math.min(50, q + 1))}
              disabled={quantity >= 50}
              aria-label={isAr ? 'زيادة العدد' : 'Increase quantity'}
            >
              +
            </button>
          </div>
        </div>
      ) : null}

      {purchaseError ? <p className="mt-3 text-xs text-destructive">{purchaseError}</p> : null}

      {!instapayPaymentId ? (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant="default"
            size="sm"
            className="rounded-xl"
            disabled={isLoading || isPurchasing !== null}
            onClick={() => void handlePurchase('myfatoorah')}
          >
            {isPurchasing === 'myfatoorah'
              ? isAr
                ? 'جارِ...'
                : '...'
              : isAr
                ? 'ادفع ببطاقة'
                : 'Pay by card'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-xl"
            disabled={isLoading || isPurchasing !== null}
            onClick={() => void handlePurchase('instapay')}
          >
            {isPurchasing === 'instapay' ? (isAr ? 'جارِ...' : '...') : 'InstaPay'}
          </Button>
        </div>
      ) : proofUploaded ? (
        <p className="mt-4 text-sm font-medium text-primary">
          {isAr ? 'تم الإرسال، بانتظار المراجعة' : 'Submitted, awaiting review'}
        </p>
      ) : (
        <div className="mt-4 space-y-3 rounded-xl border border-border/60 p-3">
          <p className="text-sm font-semibold text-foreground">
            {isAr ? 'بيانات تحويل InstaPay' : 'InstaPay transfer details'}
          </p>

          <div className="flex items-center justify-between gap-3 rounded-xl bg-muted/30 p-3">
            <div className="text-end">
              <p className="text-xs text-muted-foreground">
                {isAr ? 'معرّف InstaPay' : 'InstaPay ID'}
              </p>
              <p className="font-bold text-foreground ltr" dir="ltr">
                {INSTAPAY_RECIPIENT_ID}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-xl"
              onClick={() => void navigator.clipboard?.writeText(INSTAPAY_RECIPIENT_ID)}
            >
              {isAr ? 'نسخ' : 'Copy'}
            </Button>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-xl bg-muted/30 p-3">
            <div className="text-end">
              <p className="text-xs text-muted-foreground">
                {isAr ? 'رقم التحويل' : 'Transfer number'}
              </p>
              <p className="font-bold text-foreground ltr" dir="ltr">
                {INSTAPAY_RECIPIENT_PHONE}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-xl"
              onClick={() => void navigator.clipboard?.writeText(INSTAPAY_RECIPIENT_PHONE)}
            >
              {isAr ? 'نسخ' : 'Copy'}
            </Button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={handleProofFileChange}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-xl"
              disabled={isUploadingProof}
              onClick={() => fileInputRef.current?.click()}
            >
              <Icons.Upload className="me-2 h-4 w-4" />
              {isAr ? 'اختيار صورة الإيصال' : 'Choose receipt image'}
            </Button>
            {proofFile ? (
              <span className="max-w-[160px] truncate text-xs text-muted-foreground">
                {proofFile.name}
              </span>
            ) : null}
          </div>

          <Button
            type="button"
            className="w-full rounded-xl"
            size="sm"
            disabled={!proofFile || isUploadingProof}
            onClick={() => void handleUploadProof()}
          >
            {isUploadingProof
              ? isAr
                ? 'جارِ الرفع...'
                : 'Uploading...'
              : isAr
                ? 'إرسال الإيصال'
                : 'Submit receipt'}
          </Button>
        </div>
      )}
    </div>
  );
}
